import { merge } from 'lodash-es';
import * as yup from 'yup';

/**
 * Document Hub data schemas. `CompanyDocument` mirrors the backend
 * `companyDocument` entity; `isPossibleDuplicate`/`parentZipName` are
 * FE-provisional until the ZIP fan-out / duplicate-detection contract ships.
 */

export enum DocumentStatus {
  Uploaded = 'uploaded',
  Detecting = 'detecting',
  Detected = 'detected',
  Confirmed = 'confirmed',
  Failed = 'failed',
}

/**
 * What the uploader sees, derived by the BE from the lifecycle status and the delivery
 * timestamps. Distinct from `DocumentStatus`, which is the internal lifecycle: Analyzing
 * deliberately covers both a run in flight and one that failed, and only `dataLiveAt`
 * separates Extracting from Complete — neither of which `status` alone can express.
 */
export enum ClientDocumentStatus {
  Analyzing = 'analyzing',
  NeedsReview = 'needs_review',
  Extracting = 'extracting',
  /** A confirmed deal doc Starbreaker has not yet grouped into a package. */
  Pending = 'pending',
  /** A packaged deal doc whose package will not extract until every mandatory document arrives. */
  PendingItem = 'pending_item',
  Complete = 'complete',
}

export enum DocType {
  Financials = 'financials',
  CapTable = 'cap_table',
  DealDocs = 'deal_docs',
  BoardDeck = 'board_deck',
  EmailMemo = 'email_memo',
  Other = 'other',
  Unknown = 'unknown',
}

/** Mirrors the BE `CompanyDocumentEmailMetadata` (company.document.entity.ts). */
export interface CompanyDocumentEmailMetadata {
  intakeBatchId: string;
  from: string | null;
  recipient: string | null;
  to: string[];
  cc: string[];
  subject: string | null;
  body: string | null;
  receivedAt: string;
}

export function companyDocumentFields() {
  return {
    id: yup.number().required(),
    companyId: yup.number().nullable().defined(),
    companyName: yup.string().nullable().defined(),
    fileName: yup.string().required(),
    fileSize: yup.number().required(),
    contentType: yup.string().required(),
    s3Key: yup.string().required(),
    docType: yup
      .mixed<DocType>()
      .oneOf(Object.values(DocType) as DocType[])
      .required(),
    period: yup.string().nullable().defined(),
    roundId: yup.number().nullable().defined(),
    roundLabel: yup.string().nullable().defined(),
    roundDate: yup.string().nullable().optional(),
    // Deal-doc-only taxonomy ids (see documentHub.constants for labels/options); null for other doc types.
    eventType: yup.string().nullable().defined(),
    dealDocumentType: yup.string().nullable().defined(),
    // Starbreaker deal-doc package this deal doc belongs to; null = unpackaged.
    packageId: yup.string().nullable().defined(),
    // FullSuite delivery signals. confirmSyncedAt set means the document has left the
    // platform, which is what makes a later edit a correction rather than a first
    // confirm; companyEnrolled says whether the company is known there at all.
    confirmSyncedAt: yup.string().nullable().defined(),
    companyEnrolled: yup.boolean().required(),
    // Why detection broke. Carried from the backend but not surfaced anywhere in the UI:
    // every failure reads as Analyzing, and the reason is diagnosed from the logs.
    detectionError: yup.string().nullable().optional(),
    status: yup
      .mixed<DocumentStatus>()
      .oneOf(Object.values(DocumentStatus) as DocumentStatus[])
      .required(),
    // Optional because only the list and single-document reads derive it; the update and
    // reprocess responses return the entity untouched.
    clientStatus: yup
      .mixed<ClientDocumentStatus>()
      .oneOf(Object.values(ClientDocumentStatus) as ClientDocumentStatus[])
      .optional(),
    confidence: yup.number().nullable().defined(),
    uploadedBy: yup.string().required(),
    // The email an emailed document (uploadedBy "Email") arrived in; null/absent otherwise.
    emailMetadata: yup.mixed<CompanyDocumentEmailMetadata>().nullable().optional(),
    createdAt: yup.string().required(),
    isPossibleDuplicate: yup.boolean().optional(),
    parentZipName: yup.string().optional(),
  };
}

export function companyDocumentSchema() {
  return yup.object(companyDocumentFields());
}

export type CompanyDocument = yup.InferType<ReturnType<typeof companyDocumentSchema>>;

export function createCompanyDocument(overrides: Partial<CompanyDocument> = {}): CompanyDocument {
  return merge(
    {},
    {
      id: 0,
      companyId: null,
      companyName: null,
      fileName: '',
      fileSize: 0,
      contentType: '',
      s3Key: '',
      docType: DocType.Unknown,
      period: null,
      roundId: null,
      roundLabel: null,
      roundDate: null,
      eventType: null,
      dealDocumentType: null,
      packageId: null,
      confirmSyncedAt: null,
      companyEnrolled: true,
      detectionError: null,
      status: DocumentStatus.Uploaded,
      confidence: null,
      uploadedBy: '',
      createdAt: '',
    },
    overrides
  );
}

/** ZIP fan-out lifecycle (mirrors the BE `documentExtraction.status` enum). */
export enum ExtractionStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}

/** Wire shape of `GET /documents/extractions` — one ZIP fan-out's status record. */
export interface DocumentExtraction {
  s3Key: string;
  status: ExtractionStatus;
  totalFiles: number;
  successCount: number;
  failedCount: number;
  errorMessage: string | null;
}

/** Raw wire shape from the BE (note `fileSize` is a string). */
export interface RawCompanyDocument {
  id: number;
  companyId: number | null;
  // Present only if the docs API includes it; otherwise resolved FE-side from companyId.
  companyName?: string | null;
  // What the BE list endpoint actually calls the resolved company name.
  companyLabel?: string | null;
  fileName: string;
  fileSize: string;
  contentType: string;
  s3Key: string;
  docType: DocType;
  period: string | null;
  roundId: number | null;
  roundLabel?: string | null;
  /** Close date of the round this document is filed against; lives on the round, not the doc. */
  roundDate?: string | null;
  // Optional so the parse tolerates a BE that predates the fields.
  eventType?: string | null;
  dealDocumentType?: string | null;
  packageId?: string | null;
  confirmSyncedAt?: string | null;
  companyEnrolled?: boolean;
  detectionError?: string | null;
  uploadedBy: string;
  emailMetadata?: CompanyDocumentEmailMetadata | null;
  source: string;
  confidence: number | null;
  status: DocumentStatus;
  clientStatus?: ClientDocumentStatus;
  createdAt: string;
}
